import { Twitter, Send, Rocket, FileSearch, FileText } from "lucide-react";
import BullMark from "@/components/BullMark";
import { useLanguage } from "@/i18n/LanguageContext";
import { projectConfig, isUrlSet, explorerTokenUrl } from "@/config/project";

export default function Footer() {
  const { t } = useLanguage();
  const year = new Date().getFullYear();

  const socials = [
    isUrlSet(projectConfig.twitterUrl) && { id: "footer-link-x", icon: Twitter, href: projectConfig.twitterUrl, label: "X / Twitter" },
    isUrlSet(projectConfig.telegramUrl) && { id: "footer-link-telegram", icon: Send, href: projectConfig.telegramUrl, label: "Telegram" },
    isUrlSet(projectConfig.fourMemeUrl) && { id: "footer-link-four-meme", icon: Rocket, href: projectConfig.fourMemeUrl, label: "Four.meme" },
    { id: "footer-link-bscscan", icon: FileSearch, href: explorerTokenUrl, label: "BscScan" },
  ].filter(Boolean);

  const nav = [
    ["about", "#about", "footer-link-about"],
    ["tokenomics", "#tokenomics", "footer-link-tokenomics"],
    ["howToBuy", "#how-to-buy", "footer-link-how-to-buy"],
    ["contract", "#contract", "footer-link-contract"],
    ["faq", "#faq", "footer-link-faq"],
    ["community", "#community", "footer-link-community"],
  ];

  return (
    <footer className="border-t border-red-900/25 bg-[#050203] px-4 py-14 sm:px-6 lg:px-8" data-testid="footer-section">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-10 md:grid-cols-3">
          <div>
            <div className="mb-4 flex items-center gap-3">
              <BullMark size={38} />
              <div className="leading-tight">
                <p className="font-zh text-xl font-black text-zinc-50">{projectConfig.chineseName}</p>
                <p className="font-mono text-[10px] tracking-[0.3em] text-yellow-500">
                  {projectConfig.symbol} · {projectConfig.network}
                </p>
              </div>
            </div>
            <p className="text-sm text-zinc-500">{t("footer.tagline")}</p>
          </div>

          <div>
            <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">{t("footer.explore")}</p>
            <ul className="grid grid-cols-2 gap-2">
              {nav.map(([key, href, id]) => (
                <li key={key}>
                  <a href={href} data-testid={id} className="text-sm text-zinc-400 transition-colors hover:text-yellow-400">
                    {t(`nav.${key}`)}
                  </a>
                </li>
              ))}
            </ul>
            <a
              href={projectConfig.whitepaperUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-link-whitepaper"
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-yellow-500 transition-colors hover:text-yellow-400"
            >
              <FileText size={14} aria-hidden="true" />
              {t("nav.whitepaper")}
            </a>
            <div className="mt-5 flex gap-3">
              {socials.map((s) => {
                const Icon = s.icon;
                return (
                  <a
                    key={s.id}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    data-testid={s.id}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-red-900/40 text-zinc-400 transition-colors hover:border-yellow-600/60 hover:text-yellow-400"
                  >
                    <Icon size={16} aria-hidden="true" />
                  </a>
                );
              })}
            </div>
          </div>

          <div>
            <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">{t("footer.contractLabel")}</p>
            <a
              href={explorerTokenUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-contract-address"
              className="break-all font-mono text-xs leading-relaxed text-yellow-500/90 transition-colors hover:text-yellow-400"
            >
              {projectConfig.contractAddress}
            </a>
          </div>
        </div>

        <div className="mt-12 border-t border-red-900/20 pt-6">
          <p className="mb-3 text-xs leading-relaxed text-zinc-600" data-testid="footer-risk-line">
            {t("footer.riskLine")}
          </p>
          <p className="text-xs text-zinc-600" data-testid="footer-copyright">
            © {year} {projectConfig.chineseName} · {projectConfig.symbol}. {t("footer.rights")}
          </p>
        </div>
      </div>
    </footer>
  );
}
